import type { ProjectRole } from '@forge/shared';
import { roleAtLeast } from '@forge/shared';
import { Loader2 } from 'lucide-react';
import { Suspense, type ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth, useProjectRole } from '../lib/auth';

/** Route plumbing both surfaces share. Only the route tables differ. */

export function FullScreenLoader({ label }: { label?: string }) {
  return (
    <div className="flex h-full min-h-screen flex-col items-center justify-center gap-3 bg-ink-950">
      <Loader2 className="size-6 animate-spin text-brand-500" />
      {label && <p className="text-xs text-ink-400">{label}</p>}
    </div>
  );
}

export function Loading({ children, label }: { children: ReactNode; label?: string }) {
  return <Suspense fallback={<FullScreenLoader label={label} />}>{children}</Suspense>;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <FullScreenLoader label="Restoring your session" />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

/**
 * Route-level role gate. Someone who bookmarks an editor-only page lands on the
 * dashboards instead of an error — the nav never offered them the link anyway,
 * and the server would refuse the data regardless.
 */
export function RequireProjectRole({ minRole, children }: { minRole: ProjectRole; children: ReactNode }) {
  const { projectId } = useParams();
  const { role } = useProjectRole(projectId);
  if (!roleAtLeast(role, minRole)) {
    return <Navigate to={`/projects/${projectId}/dashboards`} replace />;
  }
  return <>{children}</>;
}

/**
 * Instance-administration gate.
 *
 * Distinct from RequireProjectRole because there is no project involved: these
 * pages act on the whole instance, so the question is what the account is, not
 * what it is in some project. Someone who bookmarks one and later loses the
 * role lands on their project list rather than an error.
 */
export function RequireSuperadmin({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (user?.globalRole !== 'superadmin') return <Navigate to="/" replace />;
  return <>{children}</>;
}
